export type ContentBlock =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "quote"; text: string; cite?: string }
  | { type: "list"; items: string[] };

export type ArticleMeta = {
  slug: string;
  title: string;
  category: string;
  categoryName: string;
  excerpt: string;
  author: string;
  authorRole: string;
  date: string;
  readTime: string;
  tag: string[];
  cover: string;
  coverAlt: string;
  editorPick?: boolean;
  hot?: boolean;
  rank?: number;
  body: ContentBlock[];
};

export const articles: ArticleMeta[] = [
  {
    slug: "pomodoro-advanced-guide",
    title: "把一天过成两天的番茄钟进阶玩法",
    category: "workflow",
    categoryName: "数字工作",
    excerpt:
      "番茄钟不是简单的计时器，而是一套关于专注与恢复的系统。这篇进阶指南会带你重新理解工作节奏，让每一段深度专注都真正创造价值。",
    author: "周屿",
    authorRole: "效率栏目主编",
    date: "09-05",
    readTime: "8 分钟",
    tag: ["时间管理", "专注力", "效率工具"],
    cover: "https://readdy.ai/api/search-image?query=Warm%20editorial%20still%20life%20of%20an%20analog%20hourglass%20and%20notebook%20on%20a%20light%20wooden%20desk%2C%20soft%20natural%20morning%20light%2C%20cream%20and%20terracotta%20tones%2C%20minimalist%20calm%20composition%2C%20high%20detail%2C%20cozy%20focused%20workspace%20atmosphere&width=1200&height=800&seq=kn-pomo-01&orientation=landscape",
    coverAlt: "番茄钟与手账摆放在书桌上，象征专注与节奏管理",
    editorPick: true,
    hot: true,
    rank: 1,
    body: [
      {
        type: "p",
        text: "很多人把番茄钟当成一个倒计时软件，25 分钟一到就放下手机、开始休息。这样做没错，但只做到了形，没做到神。真正的番茄钟，是一套把「专注」和「恢复」都纳入设计的工作系统。",
      },
      {
        type: "h2",
        text: "先搞清楚：你要管理的不是时间，是精力",
      },
      {
        type: "p",
        text: "同样一个小时，状态好的时候能完成一整段报告，状态差的时候可能连一封邮件都写得磕磕绊绊。番茄钟的价值，不在那个 25 分钟，而在它逼你承认一个事实：人不是机器，专注是有上限的，是需要被认真对待的稀缺资源。",
      },
      {
        type: "quote",
        text: "决定一天产出的，不是你在书桌前坐了多久，而是你把最好的精力留给了哪件事。",
        cite: "——《看料》效率栏目",
      },
      {
        type: "h2",
        text: "进阶玩法一：给每颗番茄命名",
      },
      {
        type: "p",
        text: "开动之前，先用一句话写下这颗番茄要完成的「可交付结果」。哪怕只是「把方案的开头两段改完」，也比「做方案」来得清晰。命名之后，你会发现走神的概率明显下降，因为你的大脑有了明确的任务锚点。",
      },
      {
        type: "h2",
        text: "进阶玩法二：把休息设计得有意义",
      },
      {
        type: "list",
        items: [
          "短休息（5 分钟）：站起来、看看远处、喝口水，尽量别刷手机。",
          "长休息（15 分钟以上）：离开屏幕，走动一下，让眼睛和肩颈真正放松。",
          "每完成四颗番茄，做一次稍长的补给，再进入下一个循环。",
        ],
      },
      {
        type: "p",
        text: "休息不是偷懒的借口，而是专注能够持续的燃料。把休息安排好，你的工作节奏才会既高效又不透支。",
      },
    ],
  },
  {
    slug: "ai-tools-right-way",
    title: "别再瞎试工具，AI 提效的正确打开方式",
    category: "workflow",
    categoryName: "数字工作",
    excerpt:
      "工具越装越多，效率却不见涨。问题不在于工具不够强，而在于你没有想清楚用它解决什么。这篇内容帮你建立一套挑选与使用 AI 工具的判断框架。",
    author: "许棠",
    authorRole: "数字工作研究员",
    date: "09-04",
    readTime: "7 分钟",
    tag: ["人工智能", "提效", "工具清单"],
    cover: "https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photography%20of%20a%20laptop%20showing%20a%20chat%20assistant%20interface%20on%20a%20warm%20cream%20desk%2C%20amber%20and%20coral%20accents%2C%20soft%20natural%20light%2C%20calm%20modern%20productivity%20scene%2C%20high%20detail%2C%20no%20people&width=1200&height=800&seq=kn-ai-02&orientation=landscape",
    coverAlt: "笔记本电脑上的 AI 助手界面与暖色办公桌面",
    editorPick: true,
    hot: true,
    rank: 2,
    body: [
      {
        type: "p",
        text: "打开任何一个效率社区，铺天盖地都是「我用 XX 工具之后效率翻倍」的帖子。你下载了十几个，试了一圈，最后发现它们大多躺在文件夹里吃灰。问题出在哪？多半出在顺序上。",
      },
      {
        type: "h2",
        text: "先锁定重复动作，再谈工具",
      },
      {
        type: "p",
        text: "AI 工具真正擅长的是替代那些有固定套路、又特别消耗时间的动作：整理会议纪要、润色邮件措辞、把零散信息归纳成提纲。先列出你每周重复做、又最不想做的三件事，再去找对应工具，比盲目尝鲜有效得多。",
      },
      {
        type: "h2",
        text: "一套可复用的判断框架",
      },
      {
        type: "list",
        items: [
          "它能直接产出接近交付水平的东西吗？还是只能给个开头？",
          "把它接入现有流程的成本，是否小于省下的时间？",
          "使用 10 次之后，你会不会越来越依赖它的初稿？",
          "数据进出是否安全，是否符合你的隐私底线？",
        ],
      },
      {
        type: "quote",
        text: "好工具是让你的手艺更好的杠杆，而不是替你思考的拐杖。",
      },
      {
        type: "p",
        text: "记住，工具永远服务于目标。想清楚要解决什么问题，比收集再多的新奇工具都重要。",
      },
    ],
  },
  {
    slug: "read-100-books",
    title: "一年读一百本书的人，靠的不是意志力",
    category: "read",
    categoryName: "阅读新知",
    excerpt:
      "读书数量从来不是目标，能不能把书里的东西变成自己的，才是关键。那些「一年读一百本」的人，其实只是把阅读拆成了轻松可持续的日常动作。",
    author: "顾言",
    authorRole: "阅读栏目编辑",
    date: "09-05",
    readTime: "9 分钟",
    tag: ["读书方法", "阅读习惯", "自我成长"],
    cover: "https://readdy.ai/api/search-image?query=Warm%20editorial%20scene%20of%20a%20stack%20of%20open%20and%20closed%20books%20on%20a%20cozy%20cream%20shelf%20with%20a%20mug%20of%20tea%2C%20golden%20afternoon%20light%2C%20terracotta%20and%20amber%20tones%2C%20inviting%20reading%20corner%2C%20high%20detail&width=1200&height=800&seq=kn-read-03&orientation=landscape",
    coverAlt: "书架上堆叠的书籍与一杯热茶，营造温暖的阅读氛围",
    editorPick: true,
    rank: 3,
    body: [
      {
        type: "p",
        text: "看见别人一年读完一百本，难免焦虑。但深入接触后你会发现，他们靠的从来不是超人般的意志力，而是一套让读书变得毫不费力的机制。意志力是稀缺品，习惯才是常青树。",
      },
      {
        type: "h2",
        text: "降低每天的门槛",
      },
      {
        type: "p",
        text: "把目标从「一周读一本书」改成「每天读 10 页」。10 页大约只要十几分钟，几乎不可能失败。可别小看这个改变：门槛越低，越容易坚持，而阅读的复利恰恰来自「一直没停」。",
      },
      {
        type: "h2",
        text: "把书放在伸手可及的地方",
      },
      {
        type: "list",
        items: [
          "床头、通勤包、书桌各放一本正在读的书。",
          "手机阅读器放一屏内可直达的阅读入口。",
          "读书和固定的场景绑定，比如睡前一小时、咖啡时刻。",
        ],
      },
      {
        type: "quote",
        text: "数量是被低估的执念，我们真正应该追求的，是把读到的变成用得上、说得清的东西。",
      },
      {
        type: "p",
        text: "读书真正的回报，不是书架上的数量，而是你思考问题时的宽度。从今天开始读 10 页，一年后你会感谢那个开始的自己。",
      },
    ],
  },
  {
    slug: "deep-reading-habits",
    title: "告别看后即忘，建立深度阅读的三个习惯",
    category: "read",
    categoryName: "阅读新知",
    excerpt:
      "刷过的内容像水一样流过，真正被记住的少之又少。深度阅读的核心不在于速度，而在于你如何把输入转化为自己的理解与表达。",
    author: "顾言",
    authorRole: "阅读栏目编辑",
    date: "09-02",
    readTime: "6 分钟",
    tag: ["深度阅读", "知识管理", "思维方式"],
    cover: "https://readdy.ai/api/search-image?query=Close%20warm%20editorial%20photograph%20of%20hands%20taking%20notes%20beside%20an%20open%20book%2C%20paper%20and%20pen%20on%20cream%20desk%2C%20soft%20natural%20window%20light%2C%20amber%20tones%2C%20focused%20study%20atmosphere%2C%20high%20detail&width=1200&height=800&seq=kn-deep-04&orientation=landscape",
    coverAlt: "翻开书页做笔记的手与暖色书桌",
    hot: true,
    body: [
      {
        type: "p",
        text: "这个时代不缺信息，缺的是把信息留存在脑子里的能力。我们常常一口气读完一篇文章，合上屏幕却想不起核心观点。想摆脱这种「看后即忘」，可以从三个小习惯开始。",
      },
      {
        type: "h2",
        text: "带着问题去读",
      },
      {
        type: "p",
        text: "开读之前先问自己：我为什么读它？我想从中得到什么？带着明确的问题，你的注意力会自动锁定相关段落，记忆的钩子也就埋下了。",
      },
      {
        type: "h2",
        text: "读完说一段给自己听",
      },
      {
        type: "list",
        items: [
          "用三句话概括核心观点。",
          "举一个自己的例子印证或反驳它。",
          "写下一个可以立刻采取的小行动。",
        ],
      },
      {
        type: "quote",
        text: "记忆不是靠重复输入建立的，而是靠主动输出时那一次费力的回忆。",
      },
      {
        type: "p",
        text: "慢一点没有关系。读得深、记得牢，往往比读得多、忘得快更有价值。把这三个习惯放进你的阅读里，让每一页都不白翻。",
      },
    ],
  },
  {
    slug: "real-rest-skills",
    title: "真正会休息的人，都在偷偷做这件事",
    category: "life",
    categoryName: "生活灵感",
    excerpt:
      "刷手机不算休息，反而是新的消耗。真正会休息的人，懂得把注意力从屏幕上移开，让大脑进入真正的放松状态。",
    author: "林简",
    authorRole: "生活方式作者",
    date: "09-05",
    readTime: "7 分钟",
    tag: ["放松", "生活方式", "注意力"],
    cover: "https://readdy.ai/api/search-image?query=Serene%20warm%20editorial%20image%20of%20a%20person%20relaxing%20on%20a%20couch%20by%20a%20sunlit%20window%20with%20a%20book%20and%20green%20plant%2C%20cream%20and%20soft%20coral%20tones%2C%20calm%20cozy%20atmosphere%2C%20soft%20focus%2C%20high%20detail%2C%20peaceful%20lifestyle&width=1200&height=800&seq=kn-rest-05&orientation=landscape",
    coverAlt: "阳光窗边看书放松的人物与绿植",
    editorPick: true,
    hot: true,
    rank: 4,
    body: [
      {
        type: "p",
        text: "下班回家瘫在沙发上刷手机，看起来是在休息，其实你的大脑一刻也没停过：短视频不断切换的信息，让你始终处于轻度兴奋和焦虑里。刷完一小时，人反而更累了。",
      },
      {
        type: "h2",
        text: "休息的本质是「切换」，不是「停摆」",
      },
      {
        type: "p",
        text: "真正的休息，是把脑力劳动切换成身体活动或沉浸式放松：出去散步、做一顿饭、读几页书、闭眼听一段音乐。关键是让大脑从「被动接收信息」切换成「专注体验当下」。",
      },
      {
        type: "h2",
        text: "给休息设置一点仪式感",
      },
      {
        type: "list",
        items: [
          "把手机放到另一个房间，物理上远离诱惑。",
          "选一件不需要意志力、只凭喜欢就能做的小事。",
          "休息结束前，给自己一个回到工作的温和提醒。",
        ],
      },
      {
        type: "quote",
        text: "会休息，是成年人最被低估的能力。",
      },
      {
        type: "p",
        text: "下次觉得累的时候，别再第一时间掏手机。试着用五分钟去楼下走走，你会发现，真正的充电往往发生在离开屏幕的那一刻。",
      },
    ],
  },
  {
    slug: "tidy-room-tidy-mind",
    title: "整理房间，也是在整理人生",
    category: "life",
    categoryName: "生活灵感",
    excerpt:
      "凌乱的桌面对应着混乱的思路。花一个下午认真整理房间，你会惊讶地发现，那些解不开的心结，也在不知不觉中被理清了。",
    author: "林简",
    authorRole: "生活方式作者",
    date: "09-01",
    readTime: "6 分钟",
    tag: ["整理收纳", "极简生活", "内心秩序"],
    cover: "https://readdy.ai/api/search-image?query=Airy%20warm%20editorial%20photograph%20of%20a%20tidy%20minimalist%20room%20with%20neatly%20folded%20blanket%20and%20organized%20shelf%2C%20cream%20and%20terracotta%20palette%2C%20soft%20natural%20light%2C%20clean%20calm%20space%2C%20high%20detail%2C%20peaceful%20living&width=1200&height=800&seq=kn-tidy-06&orientation=landscape",
    coverAlt: "整理得整洁明亮的暖色卧室一角",
    body: [
      {
        type: "p",
        text: "很多人觉得整理房间只是家务，花时间又不产生价值。可如果你认真观察会发现，房间的状态常常是内心状态的投影：内心乱糟糟的时候，桌面大概率也堆满了杂物。",
      },
      {
        type: "h2",
        text: "从「扔掉」开始，是最快的捷径",
      },
      {
        type: "p",
        text: "别急着买收纳盒。先把那些一年没碰过、用不上的东西清理出去。真正有用的大多会留下，需要断舍离的，是那些「也许以后用得到」的执念。",
      },
      {
        type: "h2",
        text: "给每件物品一个「家」",
      },
      {
        type: "list",
        items: [
          "同类物品集中摆放，固定一个位置。",
          "常用的放触手可及处，不常用的收进柜子。",
          "每次用完立刻归位，比定期大扫除省力得多。",
        ],
      },
      {
        type: "quote",
        text: "当你主动清理身边的环境，你也在悄悄告诉自己：我有能力把生活过得清爽有序。",
      },
      {
        type: "p",
        text: "整理这件事没有标准答案，唯一的标准是「你舒服」。给自己一个下午，从一张书桌开始，让空间清爽一点，也让心轻松一点。",
      },
    ],
  },
  {
    slug: "say-no-to-void-social",
    title: "远离无效社交，把时间留给重要的人",
    category: "grow",
    categoryName: "认知成长",
    excerpt:
      "社交不是越多越好。真正高质量的关系，靠的是深度而非数量。学会取舍之后，你会发现时间和精力忽然都够用了。",
    author: "陈默",
    authorRole: "成长专栏主笔",
    date: "09-04",
    readTime: "8 分钟",
    tag: ["人际关系", "时间精力", "边界感"],
    cover: "https://readdy.ai/api/search-image?query=Warm%20editorial%20image%20of%20a%20person%20walking%20alone%20on%20a%20quiet%20path%20at%20golden%20hour%20sunrise%2C%20cream%20and%20amber%20tones%2C%20reflective%20peaceful%20mood%2C%20soft%20light%2C%20high%20detail%2C%20serene%20minimal%20composition&width=1200&height=800&seq=kn-social-07&orientation=landscape",
    coverAlt: "晨曦中独自走在安静小路上的人物",
    editorPick: true,
    rank: 5,
    body: [
      {
        type: "p",
        text: "我们把大量时间花在那些「认识但不熟」的人身上：没完没了的群聊、可有可无的饭局、点赞之交的寒暄。等到夜深人静，才惊觉真正能说心里话的人，其实没几个。",
      },
      {
        type: "h2",
        text: "社交的质，远比量重要",
      },
      {
        type: "p",
        text: "心理学里的「邓巴数」提醒我们，一个人能维持的深度关系数量极其有限。与其把精力平摊给一百个人，不如把真心留给少数几个值得的人——他们会在你需要时接住你。",
      },
      {
        type: "h2",
        text: "如何温和地说「不」",
      },
      {
        type: "list",
        items: [
          "先明确自己的精力上限，别把「答应」当成默认选项。",
          "用具体理由回应，而不是含糊其辞地拖延。",
          "把省下来的时间，主动留给真正重要的人和事。",
        ],
      },
      {
        type: "quote",
        text: "边界不是冷漠，而是让真心有地方安放的前提。",
      },
      {
        type: "p",
        text: "关系的深度，从来不是靠凑数换来的。学会取舍，把时间花在值得的人身上，你会发现生活变得轻盈而笃定。",
      },
    ],
  },
  {
    slug: "retrospect-decides-ceiling",
    title: "决定你上限的，从来不是努力，是复盘",
    category: "grow",
    categoryName: "认知成长",
    excerpt:
      "埋头苦干一年，不如停下认真复盘一晚。努力让你跑得快，复盘才让你跑得远。这套简单的复盘方法，会帮你把经验真正沉淀成能力。",
    author: "陈默",
    authorRole: "成长专栏主笔",
    date: "08-30",
    readTime: "7 分钟",
    tag: ["复盘方法", "学习成长", "自我管理"],
    cover: "https://readdy.ai/api/search-image?query=Warm%20evening%20editorial%20scene%20of%20an%20open%20journal%20with%20a%20pen%20on%20a%20desk%20beside%20a%20soft%20lamp%2C%20cream%20and%20amber%20tones%2C%20cozy%20contemplative%20night%20atmosphere%2C%20high%20detail%2C%20calm%20reflection&width=1200&height=800&seq=kn-recap-08&orientation=landscape",
    coverAlt: "夜晚暖灯下的复盘手账与钢笔",
    hot: true,
    body: [
      {
        type: "p",
        text: "有一类人总是很忙，却进步缓慢；另一类人看起来从容，却一年一个台阶。差别往往不在努力程度，而在有没有认真地复盘——把经历变成经验的那一步。",
      },
      {
        type: "h2",
        text: "复盘不是检讨，是升级",
      },
      {
        type: "p",
        text: "很多人把复盘做成了自我批评大会，越想越沮丧。真正的复盘是冷静地回看：哪些做法有效？哪些环节拖了后腿？下次可以怎么调整？它关注的是系统优化，而不是指责自己。",
      },
      {
        type: "h2",
        text: "一个足够简单的复盘模板",
      },
      {
        type: "list",
        items: [
          "目标：我原本想达成什么？",
          "结果：实际发生了什么？差距在哪？",
          "原因：哪些关键因素导致了这样的结果？",
          "行动：明天/下周我要刻意改变的一件事是什么？",
        ],
      },
      {
        type: "quote",
        text: "经验不会自动变成能力，只有被复盘过的那部分才会。",
      },
      {
        type: "p",
        text: "不用等大项目结束才复盘。每周花二十分钟回看这一周，哪怕只提炼出一条要改进的行动，日积月累，也会拉开你和别人的距离。",
      },
    ],
  },
  {
    slug: "weekly-plan-no-overtime",
    title: "告别加班：一份能落地的周计划这样做",
    category: "workflow",
    categoryName: "数字工作",
    excerpt:
      "周计划列得满满当当，一周结束却发现完成的没几项。问题不是你不自律，而是计划本身就没为现实留出余地。",
    author: "周屿",
    authorRole: "效率栏目主编",
    date: "08-28",
    readTime: "6 分钟",
    tag: ["周计划", "工作方法", "时间管理"],
    cover: "https://readdy.ai/api/search-image?query=Warm%20editorial%20photograph%20of%20a%20weekly%20paper%20planner%20with%20checkmarks%20on%20a%20cream%20desk%2C%20amber%20and%20coral%20accent%2C%20soft%20natural%20light%2C%20organized%20productive%20mood%2C%20high%20detail%2C%20clean%20composition&width=1200&height=800&seq=kn-week-09&orientation=landscape",
    coverAlt: "打勾的周计划手账本与暖色办公桌面",
    body: [
      {
        type: "p",
        text: "很多人写周计划的方法，其实是把「想做的事」全部倒进一张表里，结果计划表成了压力来源。真正能落地的周计划，讲究的是取舍与缓冲。",
      },
      {
        type: "h2",
        text: "只挑三件「非做不可」的硬任务",
      },
      {
        type: "p",
        text: "一周真正能推进的深度工作，通常不超过三件。把这三件事放进计划表的核心位置，其余琐事都围绕它们安排。贪多，是所有周计划夭折的共同原因。",
      },
      {
        type: "h2",
        text: "为突发状况留白",
      },
      {
        type: "list",
        items: [
          "不要把一周填满，每天预留一到两小时的机动时间。",
          "任务按优先级而非时间顺序排布。",
          "周五下午花十分钟，顺手写下周的草稿。",
        ],
      },
      {
        type: "quote",
        text: "好的计划不是把日程填满，而是让你在变化面前依然从容。",
      },
      {
        type: "p",
        text: "下次写周计划时，先删掉一半，再开始。少即是多，留白恰恰是效率的一部分。",
      },
    ],
  },
  {
    slug: "solitude-best-charge",
    title: "独处，是成年人最好的充电方式",
    category: "life",
    categoryName: "生活灵感",
    excerpt:
      "独处不等于孤独。高质量的独处，能让你从喧嚣里抽身，听见自己真正的声音。它是疲惫生活里，一种被低估的自愈方式。",
    author: "林简",
    authorRole: "生活方式作者",
    date: "08-26",
    readTime: "6 分钟",
    tag: ["独处", "情绪自愈", "内心平静"],
    cover: "https://readdy.ai/api/search-image?query=Calm%20warm%20editorial%20image%20of%20a%20person%20sitting%20by%20a%20large%20window%20holding%20a%20cup%20of%20tea%2C%20soft%20evening%20light%2C%20cream%20and%20coral%20tones%2C%20introspective%20peaceful%20atmosphere%2C%20gentle%20light%2C%20high%20detail%2C%20serene%20mood&width=1200&height=800&seq=kn-alone-10&orientation=landscape",
    coverAlt: "窗边独坐喝茶、安静放空的人物",
    editorPick: true,
    body: [
      {
        type: "p",
        text: "成年人的世界被各种角色占满：员工、伴侣、子女、朋友。我们的注意力总是向外，几乎没留出时间向内看看自己。直到某天，你会突然很想一个人待会儿。",
      },
      {
        type: "h2",
        text: "独处是向内连接的通道",
      },
      {
        type: "p",
        text: "心理学研究发现，适当的独处能显著提升情绪调节能力与创造力。一个人走走、写写、想想，不被打扰的空白里，往往藏着最诚实的自我对话。",
      },
      {
        type: "h2",
        text: "如何拥有一段高质量的独处",
      },
      {
        type: "list",
        items: [
          "每周固定留出一小段「不被打扰」的时间。",
          "暂时放下手机，专注感受当下的一件小事。",
          "不评判、不赶时间，允许自己发一会儿呆。",
        ],
      },
      {
        type: "quote",
        text: "当你学会和自己相处，才真正拥有了无论外界如何都能站稳的能力。",
      },
      {
        type: "p",
        text: "独处不是逃避，而是一种主动的修复。给自己一点独处的时间吧，那个安静下来的你，会比想象中更有力量。",
      },
    ],
  },
];

export const getArticleBySlug = (slug: string): ArticleMeta | undefined =>
  articles.find((a) => a.slug === slug);

export const editorPicks = articles.filter((a) => a.editorPick);

export const hotArticles = articles.filter((a) => a.hot);

export const rankArticles = articles
  .filter((a) => a.rank)
  .sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99));